import { motion } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { profile } from "@/data/portfolio";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative w-full overflow-hidden pt-28 md:pt-32 pb-16 md:pb-20"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full bg-white/[0.015] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 text-sm text-zinc-500"
        >
          <span className="w-8 h-px bg-zinc-700" />
          <span data-testid="hero-overline">Portfolio · 2025 / 26</span>
        </motion.div>

        <motion.h1
          data-testid="hero-heading"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading mt-6 md:mt-8 text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] font-light tracking-[-0.035em] leading-[0.95] text-white max-w-5xl"
        >
          {profile.name}.
          <br />
          <span className="text-zinc-500">{profile.focus}.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 md:mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-zinc-400 font-light"
          data-testid="hero-intro"
        >
          {profile.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-zinc-500"
        >
          <span className="inline-flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Available for 2026 internships
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            {profile.location}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 md:mt-10 flex items-center gap-4"
        >
          <a
            href="#work"
            data-testid="hero-cta-work"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("work")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full text-sm font-medium hover:scale-[1.04] transition-transform"
          >
            See selected work
            <ArrowDown className="w-4 h-4" strokeWidth={2} />
          </a>
          <a
            href={`mailto:${profile.email}`}
            data-testid="hero-cta-email"
            className="inline-flex items-center text-sm text-zinc-300 hover:text-white border border-white/15 hover:border-white/30 px-6 py-3 rounded-full transition-colors"
          >
            {profile.email}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
